import {
  Button,
  EmptyState,
  EmptyStateIcon,
  Title,
  PageSection,
  PageSectionVariants,
  Alert,
  AlertActionCloseButton,
} from '@patternfly/react-core'
import { SearchIcon } from '@patternfly/react-icons'
import * as React from 'react'

import { getErrorLabel } from '@app/domain/patient-medication-history/patient-medication-history.helpers'
import type {
  MedicationDosageUnit,
  MedicationRoute,
  PatientMedicationHistory,
  PatientMedicationRecord,
  PatientMedication,
  PatientMedicationRecordEntry,
  MedicationFrequencyUnit,
} from '@app/domain/patient-medication-history/patient-medication-history.interface'
import type { ErrorType } from '@app/domain/patient-medication-history/patient-medication-history.rest'

import { MedicationHistoryTable } from './medication-history-table'

interface Props {
  addMedicationRecord: (activeDate: string) => void
  addMedicationRecordEntry: (
    patientMedication: PatientMedication,
    medicationRecord: PatientMedicationRecord,
  ) => void
  closeErrorAlert: () => void
  editMedication: (
    patientMedicationId: string,
    medicationDosageUnitId: string,
    medicationRouteId: string,
    name: string,
  ) => void
  deleteRecord: (medicationRecordId: string) => void
  deleteMedication: (patientMedicationId: string) => void
  editMedicationRecordEntry: (
    patientMedication: PatientMedication,
    medicationRecord: PatientMedicationRecord,
  ) => void
  medicationHistory: PatientMedicationHistory
  openAddMedicationModal: () => void
  medicationRoutes: MedicationRoute[]
  medicationDosageUnits: MedicationDosageUnit[]
  updateMedicationRecord: (
    activeDate: string,
    medicationRecord: PatientMedicationRecord,
  ) => void
  showError: boolean
  errorType: ErrorType
  medicationRecordEntries: PatientMedicationRecordEntry[]
  medicationFrequencyUnits: MedicationFrequencyUnit[]
}

export const MedicationHistory = ({
  addMedicationRecord,
  addMedicationRecordEntry,
  editMedication,
  closeErrorAlert,
  editMedicationRecordEntry,
  deleteRecord,
  deleteMedication,
  medicationHistory,
  medicationRoutes,
  medicationDosageUnits,
  openAddMedicationModal,
  updateMedicationRecord,
  showError,
  errorType,
  medicationRecordEntries,
  medicationFrequencyUnits,
}: Props) => {
  if (
    medicationHistory.medications.length > 0 ||
    medicationHistory.medicationRecords.length > 0
  ) {
    return (
      <>
        <Title headingLevel="h2" size="xl">
          Medication History
        </Title>
        <br />
        {showError ? (
          <Alert
            variant="danger"
            title={getErrorLabel(errorType)}
            aria-live="polite"
            isInline
            actionClose={<AlertActionCloseButton onClose={closeErrorAlert} />}
          />
        ) : null}

        <br />
        <PageSection variant={PageSectionVariants.light}>
          <MedicationHistoryTable
            addMedicationRecordEntry={addMedicationRecordEntry}
            addMedicationRecord={addMedicationRecord}
            editMedication={editMedication}
            editMedicationRecordEntry={editMedicationRecordEntry}
            editRecordDate={updateMedicationRecord}
            deleteRecord={deleteRecord}
            deleteMedication={deleteMedication}
            medicationHistory={medicationHistory}
            medicationRoutes={medicationRoutes}
            medicationDosageUnits={medicationDosageUnits}
            medicationRecordEntries={medicationRecordEntries}
            medicationFrequencyUnits={medicationFrequencyUnits}
          />
          <br />
          <Button variant="secondary" onClick={openAddMedicationModal}>
            Add Medication
          </Button>
        </PageSection>
      </>
    )
  } else {
    return (
      <>
        <Button variant="secondary" onClick={openAddMedicationModal}>
          Add Medication
        </Button>
        <EmptyState>
          <EmptyStateIcon icon={SearchIcon} />
          <Title headingLevel="h5" size="lg">
            No Medications Found
          </Title>
        </EmptyState>
      </>
    )
  }
}
